/**
 * Asset manager — Stage 4 art integration.
 *
 * Async-loads all generated GLB art (vehicles, cockpit, cyclist) once at boot,
 * returning a typed registry of normalized model templates. Each vehicle GLB
 * is wrapped in a Group so children can be rotated/offset without touching
 * the source scene; the wrapper carries the per-model yaw offset that points
 * the model's nose toward +Z (the sim threat travel direction). The wrapper
 * pivot is the model's BBOX CENTER (no ground lift) — `template.halfHeight`
 * tells the consumer how far to raise the pivot to plant wheels at y=0.
 *
 * Clone-on-acquire: pool slots clone `template.group.clone(true)` for an
 * independent Group sharing the source geometry/material (three's clone(true)
 * is shallow on the scene graph but shares GPU buffers — efficient for a
 * pooled fleet).
 *
 * Uses three/webgpu-compatible loading: GLTFLoader from three/addons, which
 * imports from the main `three` module (shared class identity with
 * three/webgpu). MeshoptDecoder is set for compressed GLBs.
 *
 * Shading: every loaded GLB is run through `plasticify` (render/plastic.ts) at
 * load, so the whole fleet + the cockpit + the cyclist arrive as toy-plastic
 * MeshPhysicalMaterial under the shared policy table. Doing it HERE (once, at
 * the source) rather than per clone means the crash cinematic, the rider
 * shadow proxy and every pooled vehicle inherit the look automatically. The
 * generated materials are registered as disposables alongside the GLB's own.
 */

import { Group, Box3, Vector3, Mesh, type Material, type BufferGeometry } from "three/webgpu";
import { plasticify } from "./plastic";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/addons/libs/meshopt_decoder.module.js";
import type { CarModel, TruckModel, VehicleModel } from "../sim/types";
import { TUNING } from "../sim/tuning";

// ---------------------------------------------------------------------------
// Per-model yaw offset table.
//
// All vehicle GLBs were processed by scripts/process_glb.mjs with axis
// 'longest'; the longest horizontal axis is X for every vehicle (verified via
// gltf-transform inspect bbox):
//   sedan     4.60 x 1.46 x 2.01  (X = length, nose at -X per ASSET_PIPELINE)
//   suv       4.80 x 2.28 x 2.52  (X = length; symmetric bbox, same pipeline → -X nose)
//   hatchback 4.00 x 1.90 x 2.15  (X = length; symmetric bbox, same pipeline → -X nose)
//   boxtruck  7.00 x 4.81 x 3.58  (X = length; symmetric bbox, same pipeline → -X nose)
//   semi     11.00 x 4.19 x 2.93  (X = length; symmetric bbox, same pipeline → -X nose)
//
// Threats drive toward +Z in sim space, so the model nose (-X) must rotate to
// +Z. A +90° rotation about Y maps -X → +Z, so every vehicle wrapper carries
// yawOffset = +π/2.
//
// The cyclist GLB is different: its length axis is Z (1.90 m, faces ±Z per
// ASSET_PIPELINE) — it is used only as a shadow caster and is oriented
// separately in the shadow code, NOT via this table.
// ---------------------------------------------------------------------------
const VEHICLE_YAW_OFFSET = Math.PI / 2; // +90° about Y: model -X → world +Z

/**
 * Vehicle model keys (one GLB per visual variant). Defined in the SIM
 * (src/sim/types.ts) because the sim now owns model selection — its colliders
 * and crash extents are built from the chosen model's dimensions.
 */
export type { CarModel, TruckModel, VehicleModel };

/** Final real-world vehicle dimensions in meters (target world-space bbox). */
export interface VehicleDims {
  /** Length along the sim travel axis (world Z after yaw). */
  readonly length: number;
  /** Height (world Y). */
  readonly height: number;
  /** Width (world X after yaw). */
  readonly width: number;
}

/**
 * Real-world target dimensions per vehicle model (meters). The wrapper
 * non-uniformly scales each GLB so its final world bbox matches these exactly
 * (fixes cartoonish proportions from the generated GLBs and the
 * meshopt-quantized Box3 misread that left vehicles floating at varying
 * heights). Source: real-world sedan/suv/hatchback/truck measurements.
 *
 * SINGLE SOURCE OF TRUTH: this IS the sim's `TUNING.vehicleModels` table. The
 * sim builds every vehicle's Rapier collider and its crash / near-miss extents
 * from the same numbers, so a hitbox can never drift from the art again.
 */
export const REAL_DIMS: Readonly<Record<VehicleModel, VehicleDims>> =
  TUNING.vehicleModels;

/** Raw model axis bbox measured once at load (model space, before yaw). */
export interface RawBbox {
  /** Length axis (model X). */
  readonly x: number;
  /** Height axis (model Y). */
  readonly y: number;
  /** Width axis (model Z). */
  readonly z: number;
}

/** Computed per-axis scale + ground lift for a vehicle wrapper. */
export interface VehicleScale {
  /** Scale on model X (length axis). */
  readonly sx: number;
  /** Scale on model Y (height axis). */
  readonly sy: number;
  /** Scale on model Z (width axis). */
  readonly sz: number;
  /** Half the REAL height (m); lift so wheels plant at y=0. */
  readonly halfHeight: number;
}

/**
 * Compute the non-uniform per-axis scale that maps a raw model bbox to the
 * real-world target dims, plus the ground-lift half-height. Pure function —
 * unit-tested with synthetic bboxes (GLTFLoader needs browser APIs).
 *
 * The model length axis is X; after the +90° yaw the scaled X becomes world Z
 * (length), scaled Z becomes world X (width), scaled Y stays world Y (height).
 */
export function computeVehicleScale(raw: RawBbox, target: VehicleDims): VehicleScale {
  const sx = target.length / raw.x;
  const sy = target.height / raw.y;
  const sz = target.width / raw.z;
  return { sx, sy, sz, halfHeight: target.height / 2 };
}

/** A normalized vehicle template: wrapper group + ground-lift Y + final dims. */
export interface VehicleTemplate {
  /** Wrapper group; clone this for each pool slot. Children already yawed + lifted. */
  readonly group: Group;
  /** Half the REAL bbox height (so the vehicle sits with wheels at y=0). */
  readonly halfHeight: number;
  /** Final real-world dims (m) — the target the wrapper was scaled to. */
  readonly dims: VehicleDims;
  /** The yaw offset applied inside the wrapper (for reference/debug). */
  readonly yawOffset: number;
}

/** All loaded art. `loadAll()` populates this; `null` fields mean load failed. */
export interface AssetRegistry {
  readonly vehicles: Readonly<Record<VehicleModel, VehicleTemplate>>;
  /** Cockpit GLB (handlebar + hands), not yet positioned. */
  cockpit: Group | null;
  /** Cyclist-on-bike GLB (crash cinematic; the un-rigged original). */
  cyclist: Group | null;
  /**
   * Rigged cyclist GLB — same art, legs split into FK nodes (thigh_L/R ->
   * shin_L/R). Used ONLY by the shadow caster, which pedals it procedurally
   * (render/pedalIK.ts). Null on load failure: the caster falls back to the
   * un-rigged `cyclist`, which casts a correct but static shadow.
   */
  cyclistRig: Group | null;
}

const MODEL_PATHS: Record<VehicleModel, string> = {
  sedan: "/assets/models/sedan.glb",
  suv: "/assets/models/suv.glb",
  hatchback: "/assets/models/hatchback.glb",
  boxtruck: "/assets/models/boxtruck.glb",
  semi: "/assets/models/semi.glb",
};

// Model selection now lives in the SIM (src/sim/vehicles.ts) so colliders and
// crash extents can be built from the chosen model's dimensions. The hash is
// unchanged, so the visual fleet is identical — only the authority moved.
// Re-exported for existing consumers; prefer reading `VehicleEntity.model`,
// which the sim already resolved at spawn.
export { pickCarModel, pickTruckModel, pickVehicleModel } from "../sim/vehicles";

// ---------------------------------------------------------------------------
// Loader
// ---------------------------------------------------------------------------

/** A loader that produces a typed asset registry. Async; resolve before boot. */
export class AssetManager {
  private readonly loader: GLTFLoader;
  /** All disposables (geometry + materials) across loaded GLBs, for teardown. */
  private readonly disposables: { geo: BufferGeometry; mat: Material }[] = [];
  /** Materials created by the toy-plastic conversion at load. */
  private readonly plasticMats: Material[] = [];

  constructor() {
    this.loader = new GLTFLoader();
    this.loader.setMeshoptDecoder(MeshoptDecoder);
  }

  /**
   * Load every GLB asset and return a populated registry. Rejects on a hard
   * load failure of a vehicle model; cockpit/cyclist failures are non-fatal
   * (logged + left null) so the game still runs.
   */
  async loadAll(): Promise<AssetRegistry> {
    const vehicleEntries = await Promise.all(
      (Object.keys(MODEL_PATHS) as VehicleModel[]).map(async (key) => {
        const group = await this.loadGLB(MODEL_PATHS[key], `vehicle:${key}`);
        const template = this.normalizeVehicle(group, key);
        return [key, template] as const;
      }),
    );
    const vehicles = Object.fromEntries(vehicleEntries) as Record<VehicleModel, VehicleTemplate>;

    // The three character GLBs load in PARALLEL and are individually
    // non-fatal: a failure logs and leaves that slot null rather than taking
    // the whole boot down.
    const [cockpit, cyclist, cyclistRig] = await Promise.all([
      this.loadOptional("/assets/models/cockpit.glb", "cockpit", false),
      this.loadOptional("/assets/models/cyclist.glb", "cyclist"),
      this.loadOptional("/assets/models/cyclist_rig.glb", "cyclistRig"),
    ]);

    return { vehicles, cockpit, cyclist, cyclistRig };
  }

  /** Release GPU resources held by loaded GLBs. */
  dispose(): void {
    for (const d of this.disposables) {
      d.geo.dispose();
      d.mat.dispose();
    }
    this.disposables.length = 0;
    for (const m of this.plasticMats) m.dispose();
    this.plasticMats.length = 0;
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  /** Load a non-essential GLB: registers its disposables, null on failure. */
  private async loadOptional(path: string, label: string, curve = true): Promise<Group | null> {
    try {
      const g = await this.loadGLB(path, label, curve);
      this.collectDisposables(g);
      return g;
    } catch (err) {
      console.warn(`${label} GLB failed to load (non-fatal):`, err);
      return null;
    }
  }

  private loadGLB(path: string, label: string, curve = true): Promise<Group> {
    return new Promise<Group>((resolve, reject) => {
      this.loader.load(
        path,
        (gltf) => {
          const scene = gltf.scene as Group;
          // Cel shading at the source: every consumer clones from here.
          // `curve` carries the curved-earth world bend (render/earthCurve.ts);
          // it is off for the camera-attached cockpit only.
          this.plasticMats.push(...plasticify(scene, curve));
          resolve(scene);
        },
        undefined,
        (err) => {
          console.error(`[assets] load failed (${label}): ${path}`, err);
          reject(err);
        },
      );
    });
  }

  /**
   * Wrap a vehicle GLB in a Group: non-uniformly scale the source so its final
   * world bbox matches REAL_DIMS exactly (fixes cartoonish GLB proportions and
   * the meshopt-quantized Box3 misread that floated vehicles at varying
   * heights) and rotate children by the yaw offset so the nose points +Z. The
   * wrapper pivot stays at the model's bbox CENTER (no lift); the pool
   * positions the wrapper (driving: y = halfHeight; kicked: y = body center).
   * The wrapper itself stays at the origin. The raw bbox is
   * measured ONCE here (Box3.setFromObject is used only to get the model's
   * authored proportions; the final dims come from REAL_DIMS, not this
   * measurement, so the meshopt CPU-bounds artifact cannot drift the result).
   */
  private normalizeVehicle(source: Group, key: VehicleModel): VehicleTemplate {
    this.collectDisposables(source);
    // Measure the source bbox ONCE (pivot is at bbox center from process_glb.mjs).
    // The model length axis is X, height Y, width Z (see VEHICLE_YAW_OFFSET note).
    const box = new Box3().setFromObject(source);
    const size = new Vector3();
    box.getSize(size);
    const target = REAL_DIMS[key];
    const { sx, sy, sz, halfHeight } = computeVehicleScale(
      { x: size.x, y: size.y, z: size.z },
      target,
    );

    const wrapper = new Group();
    wrapper.name = `vehicle_${key}`;
    // Non-uniform per-axis scale so the final world dims equal REAL_DIMS.
    // Scale is applied in the source's local space BEFORE the yaw rotation,
    // so scaled X (length) -> world Z, scaled Z (width) -> world X, scaled Y
    // (height) -> world Y after the +90° yaw.
    source.scale.set(sx, sy, sz);
    source.rotation.y = VEHICLE_YAW_OFFSET;
    // NO ground lift here: the wrapper pivot is the model's BBOX CENTER. The
    // consumer (vehicleView) places driving vehicles at y = halfHeight (wheels
    // plant at y=0) and kicked vehicles at the physics body center y, so the
    // tumble pivots about the center and follows the real ballistic arc.
    wrapper.add(source);
    return { group: wrapper, halfHeight, dims: target, yawOffset: VEHICLE_YAW_OFFSET };
  }

  private collectDisposables(root: Group): void {
    root.traverse((o) => {
      const m = o as Mesh;
      if (m.geometry) {
        const geo = m.geometry as BufferGeometry;
        if (!this.disposables.some((d) => d.geo === geo)) {
          const mat = m.material as Material;
          if (mat && !this.disposables.some((d) => d.mat === mat)) {
            this.disposables.push({ geo, mat });
          }
        }
      }
    });
  }
}

